import { useEffect, useRef, useState } from "react";

/**
 * Owns the syringe/fluid pump run state and the two timing loops:
 *  - a requestAnimationFrame infusion loop that advances `infused` and
 *    `elapsedMin` at the chosen sim `speed`, stopping at VTBI, and
 *  - a Web-Audio "completion" beeper driven by `alarm`.
 * Programming state (rate/vtbi/time) stays in the component and is passed in
 * as the derived numbers the loop needs.
 */
export function useInfusionPump(rateNum, vtbiNum) {
  const [running, setRunning] = useState(false);
  const [infused, setInfused] = useState(0); // mL
  const [elapsedMin, setElapsedMin] = useState(0); // simulated minutes
  const [status, setStatus] = useState("STOPPED"); // STOPPED | INFUSING | COMPLETE
  const [alarm, setAlarm] = useState(false);
  const [speed, setSpeed] = useState(120);

  const rafRef = useRef(null);
  const lastRef = useRef(null);
  const audioRef = useRef(null);
  const beepRef = useRef(null);

  /* ---------------- infusion loop ---------------- */
  useEffect(() => {
    if (!running) return;
    lastRef.current = performance.now();
    const tick = (now) => {
      const dtSec = (now - lastRef.current) / 1000;
      lastRef.current = now;
      const simHours = (dtSec * speed) / 3600;
      setInfused((prev) => {
        const next = prev + rateNum * simHours;
        if (next >= vtbiNum) {
          setRunning(false);
          setStatus("COMPLETE");
          setAlarm(true);
          return vtbiNum;
        }
        return next;
      });
      setElapsedMin((prev) => prev + (dtSec * speed) / 60);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // Deps are deliberate: the rAF loop restarts only when run/rate/vtbi/speed change.
    // Its locals (tick, dtSec, simHours) and the stable refs/setters must NOT be added.
  }, [running, rateNum, vtbiNum, speed]);

  /* ---------------- alarm beep ---------------- */
  useEffect(() => {
    if (!alarm) {
      clearInterval(beepRef.current);
      return;
    }
    const beep = () => {
      try {
        if (!audioRef.current)
          audioRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();
        const ctx = audioRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        gain.gain.value = 0.05;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.12);
      } catch (e) {
        /* audio not available */
      }
    };
    beep();
    beepRef.current = setInterval(beep, 1200);
    return () => clearInterval(beepRef.current);
  }, [alarm]);

  return {
    running,
    setRunning,
    infused,
    setInfused,
    elapsedMin,
    setElapsedMin,
    status,
    setStatus,
    alarm,
    setAlarm,
    speed,
    setSpeed,
  };
}
